import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FilmStrip, Plus, Trash, NotePencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function VideoReviews() {
  useSeo("Video Reviews", "Annotate any YouTube video with timestamped notes and drawings — private QC for your edits.");
  const [reviews, setReviews] = useState(null);
  const [url, setUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [videos, setVideos] = useState([]);

  const load = useCallback(() => api.get("/reviews").then((r) => setReviews(r.data)), []);
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
    api.get("/files").then((r) => setVideos(r.data.filter((f) => f.contentType.startsWith("video/")))).catch(() => {});
  }, [load]);

  const createFromFile = async (fileId) => {
    setCreating(true);
    try {
      const res = await api.post("/reviews", { fileId });
      setReviews([res.data, ...(reviews || [])]);
      toast.success("Review created — frame capture enabled for uploaded videos");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCreating(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/reviews", { url });
      setUrl("");
      setReviews([res.data, ...(reviews || [])]);
      toast.success("Review created");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/reviews/${id}`);
    toast.success("Review deleted");
    load();
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="video-reviews-page">
      <div className="fade-up mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--coral)] font-semibold mb-2">QC & Feedback</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Video Reviews</h1>
        <p className="text-[var(--muted)] text-base mt-3 max-w-xl">Paste a YouTube link, pause anywhere, leave timestamped notes and draw right on the frame. Share a secret link so your editor can reply.</p>
      </div>

      <form onSubmit={create} className="surface rounded-[20px] p-6 glow-ring fade-up-1">
        <label className="text-sm font-semibold block mb-2">YouTube video URL</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="review-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=... (unlisted videos work too)"
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button
            data-testid="review-create-btn"
            disabled={creating}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <Plus size={16} weight="bold" /> {creating ? "Creating..." : "Start review"}
          </button>
        </div>
        {videos.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--border-c)]">
            <p className="text-xs text-[var(--muted)] mb-2">Or review an uploaded draft from your Media Library (enables exact frame capture):</p>
            <div className="flex flex-wrap gap-2">
              {videos.slice(0, 6).map((v) => (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`review-from-file-${v.id}`}
                  onClick={() => createFromFile(v.id)}
                  disabled={creating}
                  className="px-3.5 py-1.5 rounded-full surface text-xs font-semibold hover:border-[var(--glow)] transition-colors truncate max-w-[200px] disabled:opacity-60"
                >
                  🎬 {v.filename}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>

      {reviews && reviews.length === 0 && (
        <div className="surface rounded-[20px] p-10 text-center mt-6 fade-up-2" data-testid="reviews-empty">
          <FilmStrip size={36} color="var(--muted)" weight="duotone" className="mx-auto" />
          <p className="font-bold mt-4">No reviews yet</p>
          <p className="text-[var(--muted)] text-sm mt-1.5">Paste a video link above to start your first QC pass.</p>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {reviews?.map((r, i) => (
          <div key={r.id} className={`surface rounded-[20px] p-4 flex items-center gap-4 lift group fade-up-${Math.min(i, 3)}`} data-testid={`review-row-${r.id}`}>
            <img src={r.sourceType === "file" ? undefined : `https://img.youtube.com/vi/${r.videoId}/mqdefault.jpg`} alt="" className={`w-28 rounded-[10px] aspect-video object-cover shrink-0 ${r.sourceType === "file" ? "hidden" : ""}`} />
            {r.sourceType === "file" && <div className="w-28 rounded-[10px] aspect-video bg-[var(--bg)] border border-[var(--border-c)] flex items-center justify-center shrink-0 text-lg">🎬</div>}
            <Link to={`/reviews/${r.id}`} data-testid={`review-open-${r.id}`} className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate">{r.title}</p>
              <p className="text-xs text-[var(--muted)] mt-1 flex items-center gap-1.5">
                <NotePencil size={13} /> {r.noteCount} note{r.noteCount === 1 ? "" : "s"} · {dayjs(r.createdAt).format("MMM D")}
              </p>
            </Link>
            <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--coral)] transition-opacity shrink-0">
              <Trash size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
